import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';

import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import Label from '~components/FormFields/v2/Label';
import TextInput from '~components/FormFields/v2/TextInput';
import { ComplianceDocumentType } from '~hooks/useComplianceDocumentTypes/models';

import {
  ComplianceDocumentTypeDto,
  ComplianceDocumentTypeDtoAsyncError,
  complianceDocumentTypeSchema,
} from './complianceDocumentTypeSchema';

export type ComplianceDocumentTypeFormHandler = {
  onSubmit: () => Promise<ComplianceDocumentTypeDto | undefined>;
};

type Props = {
  asyncErrors: ComplianceDocumentTypeDtoAsyncError[];
  documentType?: ComplianceDocumentType;
};

function getFieldTypeOptions() {
  return [
    {
      value: 'none',
      label: t('administration.compliance_document_types.field_type_none'),
    },
    {
      value: 'string',
      label: t('administration.compliance_document_types.field_type_text'),
    },
    {
      value: 'number',
      label: t('administration.compliance_document_types.field_type_number'),
    },
    {
      value: 'date',
      label: t('administration.compliance_document_types.field_type_date'),
    },
    {
      value: 'boolean',
      label: t('administration.compliance_document_types.field_type_boolean'),
    },
  ];
}

const ComplianceDocumentTypeForm = forwardRef<ComplianceDocumentTypeFormHandler, Props>(
  function ComplianceDocumentTypeForm({ asyncErrors, documentType }, ref) {
    const { control, handleSubmit, setError, setValue, watch } =
      useForm<ComplianceDocumentTypeDto>({
        resolver: yupResolver(complianceDocumentTypeSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
          name: documentType?.name ?? '',
          externalId: documentType?.externalId ?? '',
          description: documentType?.description ?? '',
          fieldType: documentType?.fieldType ?? 'none',
          hasExpiration: documentType?.hasExpiration ?? false,
          dispatchGating: documentType?.dispatchGating ?? false,
          requiredVendors: documentType?.requiredVendors ?? false,
          requiredInternalDrivers: documentType?.requiredInternalDrivers ?? false,
          requiredSharedDrivers: documentType?.requiredSharedDrivers ?? false,
          requiredEquipment: documentType?.requiredEquipment ?? false,
        },
      });

    useEffect(() => {
      asyncErrors.forEach((error) => {
        setError(error.field, { type: 'server', message: error.message });
      });
    }, [asyncErrors, setError]);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise((resolve) => {
            handleSubmit(
              (data) => resolve(data),
              () => resolve(undefined),
            )();
          });
        },
      }),
      [handleSubmit],
    );

    return (
      <Stack spacing={2}>
        <TextInput
          control={control}
          isRequired
          label={t('form_fields.name')}
          name="name"
        />
        <TextInput
          control={control}
          label={t('form_fields.external_id')}
          name="externalId"
        />
        <TextInput control={control} label={t('common.description')} name="description" />
        <div>
          <Label isRequired>
            {t('administration.compliance_document_types.field_type')}
          </Label>
          <Select
            fullWidth
            size="small"
            value={watch('fieldType')}
            onChange={(e) =>
              setValue('fieldType', e.target.value, {
                shouldValidate: true,
              })
            }
          >
            {getFieldTypeOptions().map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </div>
        <Box>
          <Typography variant="form-section-title" sx={{ mb: 1 }}>
            {t('administration.compliance_document_types.applies_to')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <CheckboxInput
              control={control}
              label={t('administration.compliance_document_types.required_vendors')}
              name="requiredVendors"
            />
            <CheckboxInput
              control={control}
              label={t(
                'administration.compliance_document_types.required_internal_drivers',
              )}
              name="requiredInternalDrivers"
            />
            <CheckboxInput
              control={control}
              label={t(
                'administration.compliance_document_types.required_shared_drivers',
              )}
              name="requiredSharedDrivers"
            />
            <CheckboxInput
              control={control}
              label={t('administration.compliance_document_types.required_equipment')}
              name="requiredEquipment"
            />
          </Stack>
        </Box>
        <Box>
          <Typography variant="form-section-title" sx={{ mb: 1 }}>
            {t('administration.compliance_document_types.configuration')}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Box>
              <CheckboxInput
                control={control}
                label={t('administration.compliance_document_types.has_expiration')}
                name="hasExpiration"
              />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  ml: 4,
                }}
              >
                {t('administration.compliance_document_types.has_expiration_description')}
              </Typography>
            </Box>
            <Box>
              <CheckboxInput
                control={control}
                label={t('administration.compliance_document_types.dispatch_gating')}
                name="dispatchGating"
              />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  ml: 4,
                }}
              >
                {t(
                  'administration.compliance_document_types.dispatch_gating_description',
                )}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Stack>
    );
  },
);

export default ComplianceDocumentTypeForm;
